import { useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import {
  ASSET_LOCATION_MAP_ZOOM,
  hasAssetCoordinates,
} from "@/lib/asset-data-utils.js";
import { createStatusIcon } from "@/lib/map-utils.js";
import { useLeafletReady } from "@/hooks/useLeafletReady.js";

/**
 * Single-asset Leaflet map for location context on Asset Overview.
 * @param {{
 *   latitude: number|null,
 *   longitude: number|null,
 *   status: string,
 *   assetNumber: string,
 * }} props
 * @returns {JSX.Element}
 */
export default function AssetLocationMap({
  latitude,
  longitude,
  status,
  assetNumber,
}) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const isMountedRef = useRef(true);
  const leafletReady = useLeafletReady();

  const hasCoordinates = hasAssetCoordinates(latitude, longitude);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasCoordinates || !leafletReady || !mapRef.current || !window.L) {
      return undefined;
    }

    const L = window.L;
    const container = mapRef.current;

    const cleanupMap = () => {
      if (leafletMapRef.current) {
        try {
          leafletMapRef.current.remove();
        } catch {
          // Map may already be torn down
        }
        leafletMapRef.current = null;
      }
    };

    const initializeMap = () => {
      if (
        !isMountedRef.current
        || !mapRef.current
        || !window.L
        || latitude == null
        || longitude == null
      ) {
        return;
      }

      if (
        !container.offsetParent
        || container.offsetWidth === 0
        || container.offsetHeight === 0
      ) {
        setTimeout(initializeMap, 100);
        return;
      }

      cleanupMap();
      container.innerHTML = "";

      const map = L.map(container, {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
      });

      map.setView([latitude, longitude], ASSET_LOCATION_MAP_ZOOM);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18,
        subdomains: "abc",
        noWrap: true,
        updateWhenZooming: false,
        updateWhenIdle: true,
        keepBuffer: 2,
        referrerPolicy: "strict-origin-when-cross-origin",
      }).addTo(map);

      L.control.scale().addTo(map);

      const icon = createStatusIcon(status);
      const marker = L.marker([latitude, longitude], {
        icon: icon || undefined,
      });
      marker.bindPopup(`<strong>${assetNumber}</strong>`);
      marker.addTo(map);

      leafletMapRef.current = map;

      setTimeout(() => {
        leafletMapRef.current?.invalidateSize({ animate: false });
      }, 100);
    };

    initializeMap();

    return () => {
      cleanupMap();
    };
  }, [hasCoordinates, leafletReady, latitude, longitude, status, assetNumber]);

  if (!hasCoordinates) {
    return (
      <div
        className='flex h-[240px] md:h-[280px] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/50 text-center px-4'
        data-testid='asset-location-unavailable'>
        <MapPin className='h-8 w-8 text-muted-foreground/40 mb-2' />
        <p className='text-sm text-muted-foreground'>Location unavailable</p>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      className='h-[240px] md:h-[280px] w-full rounded-lg border border-border overflow-hidden z-0'
      data-testid='asset-location-map'
    />
  );
}
